<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'next/dialog/Dialog.vue';
import CreateEnrichmentForm from './CreateEnrichmentForm.vue';

const emit = defineEmits(['created']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isOpen = ref(false);
const formRef = ref(null);
const uiFlags = useMapGetter('wgptEventAutomations/getUIFlags');

const onClose = () => {
  isOpen.value = false;
};

const open = () => {
  isOpen.value = true;
  nextTick(() => dialogRef.value?.open());
};

const close = () => {
  dialogRef.value?.close();
  onClose();
};

const handleSubmit = async () => {
  if (!formRef.value) return;
  const { validate, state } = formRef.value;
  const isValid = await validate();
  if (!isValid) return;

  try {
    const record = await store.dispatch('wgptEventAutomations/create', {
      name: state.name,
      type: state.type,
      ...(state.type === 'event_enrichment_scheduled' && state.cron
        ? { cron: state.cron }
        : {}),
      active: false,
    });
    useAlert(
      t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.API.SUCCESS_MESSAGE')
    );
    close();
    emit('created', record);
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.API.ERROR_MESSAGE')
    );
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.TITLE')"
    :description="t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.DESCRIPTION')"
    :confirm-button-label="
      t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.SAVE')
    "
    :cancel-button-label="
      t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.CANCEL')
    "
    :is-loading="uiFlags.isCreating"
    :disable-confirm-button="uiFlags.isCreating"
    @confirm="handleSubmit"
    @close="onClose"
  >
    <CreateEnrichmentForm v-if="isOpen" ref="formRef" @submit="handleSubmit" />
  </Dialog>
</template>
